
import React, { useState, useEffect, useCallback } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { supabase } from "@/integrations/supabase/client";
import { getAccountOwnershipInfo } from "./business-value-framework/accountService";
import '@xyflow/react/dist/style.css';
import { OrganizationChart } from "./org-chart/OrganizationChart";
import { ContactsTable } from "./org-chart/ContactsTable";
import { useOrgChartData } from "./org-chart/hooks/useOrgChartData";
import { useOrgChartActions } from "./org-chart/hooks/useOrgChartActions";
import { Account, CustomerContact } from "./org-chart/types";

const CustomerOrgChartTab = ({
  selectedAccount
}: {
  selectedAccount: Account | null;
}) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingContact, setEditingContact] = useState<Partial<CustomerContact>>({});
  const [user, setUser] = useState<any>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [contactToDelete, setContactToDelete] = useState<number | null>(null);
  const [canEdit, setCanEdit] = useState(false);
  const [isSupervisor, setIsSupervisor] = useState(false);
  const [ownershipLoaded, setOwnershipLoaded] = useState(false);

  // Fetch current user and account ownership info
  useEffect(() => {
    const getUser = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      setUser(user);
    };
    
    getUser();
  }, []);

  useEffect(() => {
    const loadOwnershipInfo = async () => {
      if (selectedAccount?.company_id && user) {
        const ownershipInfo = await getAccountOwnershipInfo(selectedAccount.company_id);
        setCanEdit(ownershipInfo.canEdit);
        
        // Only set as supervisor if user can view but is not the owner
        setIsSupervisor(ownershipInfo.canView && !ownershipInfo.isOwner);
        setOwnershipLoaded(true);
      } else {
        setOwnershipLoaded(false);
      }
    };

    loadOwnershipInfo();
  }, [selectedAccount, user]);

  const {
    contacts,
    setContacts,
    nodes,
    edges,
    setNodes,
    setEdges,
    selectedContactId,
    setSelectedContactId,
    onNodesChange,
    onEdgesChange,
    serializeEdges,
    updateFlowNodes,
    loadContacts,
    loadRelationships
  } = useOrgChartData(selectedAccount, user);

  const {
    handleNodeClick,
    handleNodesChange,
    onConnect,
    onEdgesDelete,
    handleSaveContact: saveContact,
    handleDeleteContact
  } = useOrgChartActions(
    selectedAccount,
    user,
    nodes,
    edges,
    setEdges,
    setContacts,
    setNodes,
    onNodesChange,
    serializeEdges,
    loadContacts,
    loadRelationships,
    updateFlowNodes,
    canEdit
  );

  // Handle node click to select contact
  const onNodeClick = useCallback((event: React.MouseEvent, node: any) => {
    event.preventDefault();
    const contactId = node.id;
    setSelectedContactId(prev => prev === contactId ? null : contactId);
  }, [setSelectedContactId]);

  const handleSaveContactWrapper = async () => {
    if (!canEdit) {
      return;
    }

    const savedContact = await saveContact(editingContact);
    
    if (savedContact) {
      setDialogOpen(false);
      setEditingContact({});

      // If this is a new contact being created, auto-select it
      if (!editingContact.id && savedContact && savedContact.length > 0) {
        setSelectedContactId(savedContact[0].id.toString());
      }
    }
  };
  
  const openDeleteDialog = (e: React.MouseEvent, id: number) => {
    e.stopPropagation();
    if (!canEdit) return;
    setContactToDelete(id);
    setDeleteDialogOpen(true);
  };

  const confirmDeleteContact = () => {
    if (contactToDelete !== null && canEdit) {
      handleDeleteContact(contactToDelete);
      setDeleteDialogOpen(false);
      setContactToDelete(null);
    }
  };

  return (
    <Card className="bg-secondary/50 backdrop-blur-lg border-2 border-primary rounded-lg shadow-lg shadow-purple-500/10 py-0 my-[12px]">
      <CardContent className="mt-4">
        <div className="flex items-center justify-between mb-4">
          <div>
            {selectedAccount && (
              <p className="text-[11px] text-primary-light">
                Selected Account ID: {selectedAccount.company_id} ({selectedAccount.company_name})
              </p>
            )}
          </div>
        </div>

        {ownershipLoaded && isSupervisor && (
          <div className="mb-2 p-1 bg-primary/20 text-primary border-primary/30 rounded-lg">
            <div className="flex items-center justify-between">
              <p className="text-xs text-secondary-800">
                You are viewing this account as a supervisor. Only the account owner can add, edit, or delete contacts.
              </p>
            </div>
          </div>
        )}
        
        <div className="grid grid-cols-2 gap-4">
          <OrganizationChart
            nodes={nodes}
            edges={edges}
            onNodesChange={handleNodesChange}
            onEdgesChange={onEdgesChange}
            onEdgesDelete={onEdgesDelete}
            onConnect={onConnect}
            onNodeClick={onNodeClick}
          />

          <ContactsTable
            contacts={contacts}
            selectedContactId={selectedContactId}
            setSelectedContactId={setSelectedContactId}
            dialogOpen={dialogOpen}
            setDialogOpen={setDialogOpen}
            editingContact={editingContact}
            setEditingContact={setEditingContact}
            handleSaveContact={handleSaveContactWrapper}
            openDeleteDialog={openDeleteDialog}
            canEdit={canEdit}
          />
        </div>
      </CardContent>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirm Deletion</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this contact? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => {
              setDeleteDialogOpen(false);
              setContactToDelete(null);
            }}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDeleteContact}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
};

export default CustomerOrgChartTab;
